import type { FC, ReactNode } from 'react';

import { COMMUNITY_FEED_OVERLAY_OFFSET_CLASS } from '~/constants/community';
import useIsMobile from '~/hooks/useIsMobile';

import { BottomSheetDialog } from './ui/BottomSheetDialog';
import { Dialog } from './ui/Dialog';
import { ModalBrandHeader } from './ui/ModalBrandHeader';
import { ModalCloseButton } from './ui/ModalCloseButton';

import { useCommunityLogoUrl } from '../_hooks/useCommunityLogoUrl';

interface CommunityAuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  description: string;
  closeLabel: string;
  publicationName: string;
  children: ReactNode;
  footer?: ReactNode;
  // Skip the enter animation on first mount so the modal is visible on first paint.
  disableInitialTransition?: boolean;
}

export const CommunityAuthModal: FC<CommunityAuthModalProps> = ({
  isOpen,
  onClose,
  title,
  description,
  closeLabel,
  publicationName,
  children,
  footer,
  disableInitialTransition = false,
}) => {
  const isMobile = useIsMobile();
  const logoUrl = useCommunityLogoUrl();

  const body = (
    <div className="flex w-full max-w-[320px] flex-col items-center gap-8">
      <ModalBrandHeader
        imageUrl={logoUrl}
        imageAlt={`${publicationName} logo`}
        title={title}
        description={description}
      />

      {children}

      {footer}
    </div>
  );

  if (isMobile) {
    return (
      <BottomSheetDialog
        isOpen={isOpen}
        onClose={onClose}
        disableInitialTransition={disableInitialTransition}
        absolutePanel
        panelClassName="flex flex-col overflow-hidden bg-white shadow-xl"
      >
        <div className="flex h-14 shrink-0 items-center justify-end py-2 pl-3 pr-2">
          <ModalCloseButton onClose={onClose} label={closeLabel} className="p-2" />
        </div>
        <div className="flex flex-col items-center px-6 py-10">{body}</div>
      </BottomSheetDialog>
    );
  }

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      disableInitialTransition={disableInitialTransition}
      containerClassName={COMMUNITY_FEED_OVERLAY_OFFSET_CLASS}
      className="relative flex w-full max-w-[460px] min-h-[520px] flex-col rounded-[24px] bg-white shadow-xl"
    >
      <ModalCloseButton onClose={onClose} label={closeLabel} className="absolute right-4 top-4 size-8" />
      <div className="flex flex-1 flex-col items-center justify-center px-6 py-8 sm:px-16">{body}</div>
    </Dialog>
  );
};
